// FreeReportDialog.js — Email-entry dialog for the "Get one free report" flow.
//
// UX flow on this side:
//   • User clicks the "Get one free report" CTA on the homepage.
//   • This Shadcn Dialog opens with a single email input.
//   • Submitting POSTs to /api/free-report/request, then either:
//       (a) shows "Check your inbox" with a sub-headline reminding them
//           the link is single-use and expires in 24h, OR
//       (b) shows the "already used today" state with a $1 upgrade CTA.
//   • The actual magic-link verification + sessionStorage handoff is
//     done back in App.js when the user returns from the email click
//     with a `?free=<id>` URL param.
//
// Privacy framing is woven into the form copy on purpose — first-time
// users need the reassurance to give an email at all.
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATES = {
  IDLE: 'idle',
  SENDING: 'sending',
  SENT: 'sent',
  ALREADY_USED: 'already_used',
  ERROR: 'error',
};

export function FreeReportDialog({ open, onOpenChange, onUpgradeClick }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState(STATES.IDLE);
  const [message, setMessage] = useState('');

  const reset = () => {
    setEmail('');
    setState(STATES.IDLE);
    setMessage('');
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state === STATES.SENDING) return;
    setState(STATES.SENDING);
    setMessage('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/free-report/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await r.json();
      setMessage(data.message || '');
      switch (data.status) {
        case 'sent':
          setState(STATES.SENT);
          break;
        case 'already_used':
          setState(STATES.ALREADY_USED);
          break;
        case 'invalid_email':
        case 'disposable_blocked':
        case 'ip_rate_limited':
        case 'send_failed':
        default:
          setState(STATES.ERROR);
          break;
      }
    } catch (err) {
      console.error('Free report request failed:', err);
      setState(STATES.ERROR);
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="free-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'hsl(var(--gb-navy))' }}>
            <Mail className="h-5 w-5" style={{ color: 'hsl(var(--gb-gold))' }} />
            Get a free report
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: 'hsl(var(--gb-text-muted))' }}>
            Enter your email and we'll send a one-click unlock link.
            Single-use.
          </DialogDescription>
        </DialogHeader>

        {/* Idle / sending — show the form */}
        {(state === STATES.IDLE || state === STATES.SENDING || state === STATES.ERROR) && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="free-report-form">
            <div className="space-y-2">
              <Label htmlFor="free-email" style={{ color: 'hsl(var(--gb-navy))' }}>
                Email address
              </Label>
              <Input
                id="free-email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={state === STATES.SENDING}
                data-testid="free-report-email-input"
              />
            </div>
            {state === STATES.ERROR && message && (
              <div
                className="flex items-start gap-2 rounded-md p-3 text-sm"
                style={{
                  backgroundColor: 'hsl(var(--gb-rust) / 0.08)',
                  color: 'hsl(var(--gb-rust))',
                  border: '1px solid hsl(var(--gb-rust) / 0.3)',
                }}
                data-testid="free-report-error"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{message}</span>
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                data-testid="free-report-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={state === STATES.SENDING || !email.trim()}
                data-testid="free-report-submit"
                style={{
                  backgroundColor: 'hsl(var(--gb-navy))',
                  color: 'hsl(var(--gb-primary-foreground))',
                }}
              >
                {state === STATES.SENDING ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  'Send unlock link'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Sent — magic link is in their inbox */}
        {state === STATES.SENT && (
          <div className="space-y-4 py-2" data-testid="free-report-sent">
            <div
              className="flex items-start gap-3 rounded-md p-4"
              style={{
                backgroundColor: 'hsl(var(--gb-gold) / 0.10)',
                border: '1px solid hsl(var(--gb-gold) / 0.5)',
              }}
            >
              <CheckCircle2
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                style={{ color: 'hsl(var(--gb-gold))' }}
              />
              <div>
                <div className="text-sm font-medium" style={{ color: 'hsl(var(--gb-navy))' }}>
                  Check your inbox.
                </div>
                <div
                  className="text-sm mt-1 leading-relaxed"
                  style={{ color: 'hsl(var(--gb-text))' }}
                >
                  We sent a one-click unlock link to <strong>{email}</strong>. The link
                  is single-use.
                </div>
              </div>
            </div>
            <p className="text-xs" style={{ color: 'hsl(var(--gb-text-muted))' }}>
              Tip: if you don't see it, check your spam folder, or add
              info@fruchet.com to your contacts.
            </p>
            <DialogFooter>
              <Button
                onClick={() => handleClose(false)}
                data-testid="free-report-close-sent"
                style={{
                  backgroundColor: 'hsl(var(--gb-navy))',
                  color: 'hsl(var(--gb-primary-foreground))',
                }}
              >
                Got it
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Already used in last 24h — upsell to $1 */}
        {state === STATES.ALREADY_USED && (
          <div className="space-y-4 py-2" data-testid="free-report-already-used">
            <div
              className="flex items-start gap-3 rounded-md p-4"
              style={{
                backgroundColor: 'hsl(var(--gb-gold) / 0.08)',
                border: '1px solid hsl(var(--gb-gold) / 0.4)',
              }}
            >
              <AlertCircle
                className="h-5 w-5 flex-shrink-0 mt-0.5"
                style={{ color: 'hsl(var(--gb-gold))' }}
              />
              <div>
                <div className="text-sm font-medium" style={{ color: 'hsl(var(--gb-navy))' }}>
                  You've already used your free sample.
                </div>
                <div
                  className="text-sm mt-1 leading-relaxed"
                  style={{ color: 'hsl(var(--gb-text))' }}
                >
                  Unlock another report right now for just $1.
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                data-testid="free-report-close-already"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  handleClose(false);
                  if (onUpgradeClick) onUpgradeClick();
                }}
                data-testid="free-report-upgrade"
                style={{
                  backgroundColor: 'hsl(var(--gb-navy))',
                  color: 'hsl(var(--gb-primary-foreground))',
                }}
              >
                Unlock for $1
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FreeReportDialog;
